import { useCallback, useEffect, useState } from "react";
import { StageConfig, StageKey } from "./integrationConfigData";
import { AuthTypeApi } from "../pages/payerIntegration/types/providerIntegrationtypes";
import {
  loadStageConfig,
  loadStageConfigByAuthType,
} from "../utils/integrationApiHelpers";

export type LoadedStage = StageConfig & { configurationId?: number };

/**
 * Loads the active PA configuration on mount (no authTypeId).
 * Use reloadForAuthType when the user changes the auth type dropdown.
 *
 * configurationTypeId:
 * - undefined — stage does not use config types; load immediately
 * - null — stage uses types but none selected yet; wait
 * - number — load with that type id
 */
export const useStageConfigLoader = (
  providerIdRaw: string,
  stageKey: StageKey,
  authTypes: AuthTypeApi[],
  fallback?: StageConfig,
  configurationTypeId?: number | null,
) => {
  const [loadedStage, setLoadedStage] = useState<LoadedStage | undefined>(
    fallback,
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const providerId = Number(providerIdRaw);

  const reloadForAuthType = useCallback(
    async (authTypeId: number) => {
      if (!providerId || !authTypeId) return;
      if (configurationTypeId === null) return;

      setIsLoading(true);
      setLoadError(null);

      try {
        const config = await loadStageConfigByAuthType(
          providerId,
          stageKey,
          authTypeId,
          configurationTypeId,
        );
        setLoadedStage(config);
      } catch {
        setLoadError("Unable to load configuration.");
        setLoadedStage(fallback);
      } finally {
        setIsLoading(false);
      }
    },
    [providerId, stageKey, fallback, configurationTypeId],
  );

  useEffect(() => {
    if (!providerId) return;

    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);

    const run = async () => {
      if (configurationTypeId === null) {
        if (!cancelled) setIsLoading(false);
        return;
      }

      try {
        const config = await loadStageConfig(
          providerId,
          stageKey,
          configurationTypeId,
        );
        if (!cancelled) setLoadedStage(config);
      } catch {
        if (!cancelled) {
          setLoadError("Unable to load configuration.");
          setLoadedStage(fallback);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [providerIdRaw, stageKey, configurationTypeId]);

  return {
    loadedStage,
    setLoadedStage,
    loadError,
    isLoading,
    reloadForAuthType,
  };
};

export const secretOk = (
  value: string,
  hasExistingConfig: boolean,
  previouslyConfigured?: boolean,
) => Boolean(value.trim()) || (hasExistingConfig && previouslyConfigured);
